import { BLUE_LOADING_BUTTON } from "portal/theme/theme";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Form, Formik } from "formik";
import { GlobalHotKeys } from "react-hotkeys";
import {
  SetPlantProfileForm,
  usePlantProfileFormConfig,
} from "./form/SetPlantProfileForm";
import { UnsavedPlantProfile } from "portal/utils/categoryCollection/plantProfile";
import { useTranslation } from "react-i18next";
import React, { FC, useState } from "react";

interface SetPlantProfileProps {
  triggerButton: (onClick: () => void) => React.ReactNode;
  hotkey?: {
    sequence: string;
    key: string;
  };
  title: string;
  okText: string;
  okIcon?: JSX.Element;
  initialValue?: UnsavedPlantProfile;
  onSubmit: (value: UnsavedPlantProfile) => void;
  customerUuid?: string;
  nameOnly?: boolean;
}

const _SetPlantProfile: FC<SetPlantProfileProps> = ({
  triggerButton,
  hotkey,
  title,
  okText,
  okIcon,
  initialValue,
  onSubmit,
  customerUuid,
  nameOnly = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState<boolean>(false);

  const handleSubmit = (value: UnsavedPlantProfile): void => {
    setOpen(false);
    onSubmit(value);
  };

  const formConfig = usePlantProfileFormConfig(initialValue, handleSubmit, t, {
    nameOnly,
  });

  return (
    <>
      {hotkey && (
        <GlobalHotKeys
          keyMap={{
            [hotkey.key]: {
              name: title,
              action: "keyup",
              sequence: hotkey.sequence,
            },
          }}
          handlers={{
            [hotkey.key]: () => setOpen(true),
          }}
        />
      )}
      {triggerButton(() => {
        setOpen(true);
      })}
      <Dialog
        open={isOpen}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            className: "min-w-1/3",
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        {isOpen && (
          <Formik {...formConfig}>
            {({ submitForm, isSubmitting, dirty, isValid }) => (
              <Form>
                <DialogContent>
                  <SetPlantProfileForm
                    customerUuid={customerUuid}
                    nameOnly={nameOnly}
                  />
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="text"
                    color="inherit"
                    disabled={isSubmitting}
                    onClick={() => setOpen(false)}
                  >
                    {t("utils.actions.cancel")}
                  </Button>
                  <Button
                    {...BLUE_LOADING_BUTTON}
                    disabled={!dirty || !isValid}
                    loading={isSubmitting}
                    onClick={submitForm}
                    startIcon={okIcon}
                  >
                    {okText}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </>
  );
};

export const SetPlantProfile = _SetPlantProfile;
